<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query';
import { ref, computed } from 'vue';
import { taskDefinitionsApi, type TaskDefinition } from '@/api/task-definitions';
import { taskCategoriesApi } from '@/api/task-categories';
import { usePermissions } from '@/composables/usePermissions';
import { useResponsiveTable } from '@/composables/useResponsiveTable';
import ListToolbar from '@/components/list-toolbar.vue';
import TaskDefinitionForm from './task-definition-form.vue';
import TaskDefinitionDiscardForm from './task-definition-discard-form.vue';
import TaskDefinitionUndiscardForm from './task-definition-undiscard-form.vue';

const { hasPermission } = usePermissions();
const { tableProps } = useResponsiveTable();

const selectedDefinition = ref<TaskDefinition | null>(null);
const selectedCategoryId = ref<number | null>(null);
const showForm = ref(false);
const scope = ref<'active' | 'discarded'>('active');
const hasActions = hasPermission.value('tasks', 'update') || hasPermission.value('tasks', 'destroy');
const { data: taskCategories } = useQuery({
  queryKey: ['taskCategories', 'active'],
  queryFn: () => taskCategoriesApi.index('active'),
  initialData: [],
});

const {
  data: activeTaskDefinitions,
  refetch: refetchActiveDefinitions,
  isPending: isLoadingActiveDefinitions,
} = useQuery({
  queryKey: ['taskDefinitions', 'active', selectedCategoryId],
  queryFn: () => taskDefinitionsApi.index(selectedCategoryId.value || undefined, 'active'),
  initialData: [],
});

const {
  data: discardedTaskDefinitions,
  refetch: refetchDiscardedDefinitions,
  isPending: isLoadingDiscardedDefinitions,
} = useQuery({
  queryKey: ['taskDefinitions', 'discarded', selectedCategoryId],
  queryFn: () => taskDefinitionsApi.index(selectedCategoryId.value || undefined, 'discarded'),
  initialData: [],
});

const taskDefinitions = computed(() =>
  (scope.value === 'active' ? activeTaskDefinitions.value : discardedTaskDefinitions.value),
);

const filteredDefinitions = computed(() => {
  if (!selectedCategoryId.value) return taskDefinitions.value;

  return taskDefinitions.value.filter(
    definition => definition.taskCategoryId === selectedCategoryId.value,
  );
});

const isLoading = computed(() => isLoadingActiveDefinitions.value || isLoadingDiscardedDefinitions.value);

const actionSuccess = ref(false);

function openCreateForm() {
  selectedDefinition.value = null;
  showForm.value = true;
}

function openEditForm(definition: TaskDefinition) {
  selectedDefinition.value = definition;
  showForm.value = true;
}

function handleDiscardAndUndiscardSuccess() {
  refetchActiveDefinitions();
  refetchDiscardedDefinitions();
  actionSuccess.value = true;
}

function handleFormSuccess() {
  actionSuccess.value = true;
}

function handleFormClose() {
  showForm.value = false;
  selectedDefinition.value = null;
}

function clearCategoryFilter() {
  selectedCategoryId.value = null;
}

const headers: { title: string, key: string, sortable?: boolean }[] = [
  { title: 'Nombre', key: 'name' },
  { title: 'Descripción', key: 'description' },
  { title: 'Categoría', key: 'taskCategoryName' },
  { title: 'Creada', key: 'humanCreatedAt' },
];
if (hasActions) {
  headers.push({ title: 'Acciones', key: 'actions', sortable: false });
}
</script>
<template>
  <v-card>
    <v-data-table
      v-bind="tableProps"
      :headers="headers"
      :header-props="{ class: 'font-weight-bold' }"
      :items="filteredDefinitions"
      :loading="isLoading"
      loading-text="Cargando definiciones de tareas..."
    >
      <template #top>
        <list-toolbar title="Definiciones de Tareas">
          <template #actions>
            <v-btn
              v-if="scope === 'active' && hasPermission('tasks', 'create')"
              class="text-white border bg-primary"
              @click="openCreateForm"
            >
              <v-icon
                start
                icon="mdi-plus"
              />
              Agregar Tarea
            </v-btn>
          </template>
          <template #tabs>
            <v-tabs
              v-model="scope"
              color="primary"
            >
              <v-tab
                value="active"
                class="rounded-pill text-body-1"
              >
                Activas
              </v-tab>
              <v-tab
                value="discarded"
                class="rounded-pill text-body-1"
              >
                Archivadas
              </v-tab>
            </v-tabs>
          </template>
        </list-toolbar>
        <div class="mx-4 my-4">
          <v-row>
            <v-col
              cols="12"
              md="6"
            >
              <v-select
                v-model="selectedCategoryId"
                :items="taskCategories"
                item-title="name"
                item-value="id"
                label="Filtrar por categoría"
                variant="outlined"
                density="compact"
                clearable
                @click:clear="clearCategoryFilter"
              >
                <template #prepend-inner>
                  <v-icon icon="mdi-filter" />
                </template>
              </v-select>
            </v-col>
          </v-row>
        </div>
      </template>
      <template #[`item.description`]="{ item }">
        <p class="text-wrap description-cell">
          {{ item.description || '—' }}
        </p>
      </template>
      <template #[`item.taskCategoryName`]="{ item }">
        <v-chip
          color="primary"
          size="small"
          variant="outlined"
        >
          {{ item.taskCategoryName }}
        </v-chip>
      </template>
      <template #[`item.actions`]="{ item }">
        <div
          v-if="scope === 'active' && hasActions"
          class="d-flex"
        >
          <v-btn
            v-if="hasPermission('tasks', 'update')"
            icon="mdi-pencil"
            size="small"
            variant="text"
            @click="openEditForm(item)"
          />
          <v-dialog
            max-width="600"
            scrollable
          >
            <template #activator="{ props: activatorProps }">
              <v-btn
                v-if="hasPermission('tasks', 'destroy')"
                v-bind="activatorProps"
                icon="mdi-archive"
                size="small"
                variant="text"
                color="warning"
              />
            </template>
            <template #default="{ isActive }">
              <task-definition-discard-form
                :task-definition="item"
                @close="isActive.value = false"
                @success="handleDiscardAndUndiscardSuccess"
              />
            </template>
          </v-dialog>
        </div>
        <v-dialog
          v-else-if="scope === 'discarded' && hasActions"
          max-width="600"
          scrollable
        >
          <template #activator="{ props: activatorProps }">
            <v-btn
              v-if="hasPermission('tasks', 'update')"
              v-bind="activatorProps"
              icon="mdi-package-up"
              size="small"
              variant="text"
              color="success"
            />
          </template>
          <template #default="{ isActive }">
            <task-definition-undiscard-form
              :task-definition="item"
              @close="isActive.value = false"
              @success="handleDiscardAndUndiscardSuccess"
            />
          </template>
        </v-dialog>
      </template>
    </v-data-table>

    <v-dialog
      v-model="showForm"
      max-width="600"
      scrollable
    >
      <task-definition-form
        :task-definition="selectedDefinition"
        @close="handleFormClose"
        @success="handleFormSuccess"
      />
    </v-dialog>
  </v-card>

  <v-snackbar
    v-model="actionSuccess"
    :timeout="2000"
    color="success"
    rounded="pill"
  >
    <v-icon
      icon="mdi-check-circle"
      class="mr-4"
    />
    Cambios guardados exitosamente!
  </v-snackbar>
</template>
<style scoped>
.description-cell {
  max-width: 300px;
}
</style>
